/**
 * space/celestial.ts — Celestial body mesh factory
 * M3.2: km units — radii in km directly, no scale factor.
 * Sun gets toneMapped:false + fog:false + additive glow sprite.
 */

import * as THREE from 'three';
import { PHYSICAL, type BodyId } from '../sim/ephemeris.js';

export interface CelestialBody {
  id: BodyId;
  mesh: THREE.Mesh;
  radiusKm: number;
  lodLevel: number;
}

export function createCelestialBody(id: BodyId, color: number): CelestialBody {
  const phys = PHYSICAL[id];
  const radiusKm = phys.radiusKm; // km directly

  const geo = new THREE.SphereGeometry(radiusKm, 48, 32);
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.9,
    metalness: 0.0,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.userData.bodyId = id;
  mesh.userData.radiusKm = phys.radiusKm;

  return { id, mesh, radiusKm: phys.radiusKm, lodLevel: 1 };
}

/**
 * Create the Sun's emissive disc + 4× radius additive glow sprite.
 * toneMapped:false keeps the disc blinding through ACES.
 * All sizes in km.
 */
export function createSunMesh(): { disc: THREE.Mesh; glow: THREE.Sprite } {
  const phys = PHYSICAL['sun'];
  const radiusKm = phys.radiusKm;

  // Disc: emissive, never tone-mapped, no fog
  const discGeo = new THREE.SphereGeometry(radiusKm, 64, 48);
  const discMat = new THREE.MeshBasicMaterial({
    color: 0xfff8e7,
    toneMapped: false,
    fog: false,
  });
  const disc = new THREE.Mesh(discGeo, discMat);
  disc.userData.bodyId = 'sun';
  disc.userData.radiusKm = phys.radiusKm;

  // Glow sprite: 8× radius, additive, radial gradient
  const glowSizeKm = radiusKm * 8;
  const glowCanvas = document.createElement('canvas');
  glowCanvas.width = 256;
  glowCanvas.height = 256;
  const gctx = glowCanvas.getContext('2d')!;
  const gradient = gctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  gradient.addColorStop(0, 'rgba(255,248,220,0.4)');
  gradient.addColorStop(0.2, 'rgba(255,200,100,0.2)');
  gradient.addColorStop(0.5, 'rgba(255,150,50,0.05)');
  gradient.addColorStop(1, 'rgba(255,100,20,0)');
  gctx.fillStyle = gradient;
  gctx.fillRect(0, 0, 256, 256);

  const glowTex = new THREE.CanvasTexture(glowCanvas);
  const glowMat = new THREE.SpriteMaterial({
    map: glowTex,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    toneMapped: false,
    fog: false,
    transparent: true,
    opacity: 0.8,
  });
  const glow = new THREE.Sprite(glowMat);
  glow.scale.set(glowSizeKm, glowSizeKm, 1);
  glow.userData.bodyId = 'sun-glow';

  return { disc, glow };
}

export const BODY_COLORS: Record<string, number> = {
  sun:      0xfff8e7,
  mercury:  0xb5b5b5,
  venus:    0xe8d9a0,
  earth:    0x4488cc,
  moon:     0xcccccc,
  mars:     0xc1440e,
  jupiter:  0xd4b896,
  saturn:   0xe8d5a3,
  uranus:   0x9ec8d9,
  neptune:  0x4466ee,
  pluto:    0xd4c8b4,
};
